import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import LeftMenu from '../../components/leftMenu';
import Pagination from '../../components/pagination';
import SmsVerifyModal from '../../components/SmsVerifyModal';
import translations from '../../data/translation';
import useAxiosFetch from '../../hooks/useAxiosFetch';
import useStoreNo from '../../hooks/useStoreNo';
import { Config } from '../../common/config';

const EMPTY_FORM = {
  adminId: '',
  password: '',
  passwordConfirm: '',
  adminName: '',
  mobile: '',
};

export default function StaffManage() {
  const { fetchData } = useAxiosFetch();
  const storeNo = useStoreNo();
  const language = useSelector(state => state.language.language);
  const userInfo = useSelector(state => state.userInfo);
  const t = translations[language];
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [list, setList] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [idCheckedValue, setIdCheckedValue] = useState('');
  const [idCheckPassed, setIdCheckPassed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  // 비밀번호 확인 검사 대상 여부 — 수정 모드에서 비밀번호를 비워두면 변경 의사가 없는
  //  것이므로(placeholder '변경 시에만 입력') 확인란도 검사하지 않는다.
  const passwordTouched = !!form.password;
  const passwordMatched =
    !passwordTouched || form.password === form.passwordConfirm;
  // 신규 등록 시에만 SMS 본인확인 강제 — verifySms(SignupVerification 테이블)는
  //  이미 storeNo가 있는 기존 Admin row에 대해선 ConflictException을 던지도록 돼있어
  //  (sms-mail.service.ts sendSmsAuth), 수정 화면(기존 직원)에는 적용할 수 없다.
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [verifiedMobile, setVerifiedMobile] = useState('');
  const mobileVerified =
    !!form.mobile.trim() && form.mobile.trim() === verifiedMobile;
  const isOwner =
    userInfo?.role === 'AG001' ||
    (Array.isArray(userInfo?.roles) && userInfo.roles.includes('admin'));

  const loadList = async (page = currentPage) => {
    const params = new URLSearchParams();
    params.set('pageNo', String(page));
    params.set('pageSize', '10');
    params.set('sortOrd', 'desc');
    if (keyword.trim()) params.set('keyword', keyword.trim());
    const ret = await fetchData({
      url: `${Config.API_SERVER}staff?${params.toString()}`,
      method: 'get',
    });
    const data = ret?.data?.data;
    setList(Array.isArray(data?.list) ? data.list : []);
    setTotalCount(data?.total ?? 0);
    setTotalPage(data?.totalPage ?? 1);
  };

  useEffect(() => {
    loadList(1);
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  useEffect(() => {
    loadList(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const onSubmit = async () => {
    let ret;
    const payload = {
      adminId: form.adminId.trim(),
      password: form.password,
      adminName: form.adminName.trim(),
      mobile: form.mobile.trim(),
    };
    if (!payload.adminName) return window.alert('이름을 입력해 주세요.');
    // 서버 검증 범위(아이디·이름 80자, 비밀번호 8~64자)를 넘으면 영어 오류가 뜨므로 먼저 막는다.
    if (payload.adminId.length > 80) return window.alert('아이디는 80자 이하로 입력해 주세요.');
    if (payload.adminName.length > 80) return window.alert('이름은 80자 이하로 입력해 주세요.');
    if (payload.password && payload.password.length < 8) {
      return window.alert('비밀번호는 8자 이상 입력해 주세요.');
    }
    if (payload.password && payload.password.length > 64) {
      return window.alert('비밀번호는 64자 이하로 입력해 주세요.');
    }
    // 비밀번호를 입력했다면(신규는 항상) 확인란과 일치해야 한다.
    if (!passwordMatched) {
      return window.alert('비밀번호가 일치하지 않습니다.');
    }
    if (!editingId) {
      if (!payload.mobile) return window.alert('휴대폰 전화번호를 입력해 주세요.');
      if (!payload.adminId) return window.alert('아이디를 입력해 주세요.');
      if (!idCheckPassed || idCheckedValue !== payload.adminId) {
        return window.alert('아이디 중복확인을 먼저 진행해 주세요.');
      }
      if (payload.mobile !== verifiedMobile) {
        return window.alert('휴대폰 번호 인증을 완료해 주세요.');
      }
      if (!payload.password || payload.password.length < 8) {
        return window.alert('비밀번호는 8자 이상 입력해 주세요.');
      }
      ret = await fetchData({
        url: `${Config.API_SERVER}staff`,
        method: 'post',
        data: payload,
      });
    } else {
      const updatePayload = { ...payload };
      delete updatePayload.adminId;
      if (!updatePayload.password) delete updatePayload.password;
      ret = await fetchData({
        url: `${Config.API_SERVER}staff/${editingId}`,
        method: 'put',
        data: updatePayload,
      });
    }
    if (ret?.status >= 400) {
      if (ret.status === 409) {
        window.alert('이미 사용 중인 아이디입니다.');
      } else {
        window.alert(ret?.data?.message || '직원 저장 중 오류가 발생했습니다.');
      }
      return;
    }
    setForm(EMPTY_FORM);
    setEditingId('');
    setIdCheckedValue('');
    setIdCheckPassed(false);
    setVerifiedMobile('');
    await loadList(currentPage);
  };

  const handleSmsSend = async () => {
    const adminId = form.adminId.trim();
    if (!adminId) throw new Error('아이디를 먼저 입력해 주세요.');
    if (!form.mobile.trim()) throw new Error(t.smsVerifyNoMobile);
    const ret = await fetchData({
      url: `${Config.API_SERVER}mail/send-sms`,
      method: 'post',
      data: { adminId, mobile: form.mobile.trim() },
    });
    // ret이 undefined인 경우(네트워크 오류·타임아웃·세션 만료 401)도 실패로 처리한다.
    //  안 그러면 SMS가 실제로 발송되지 않았는데도 발송 성공으로 오인시킨다.
    if (!ret || ret.status >= 400) {
      throw new Error(ret?.data?.message || 'SMS 발송에 실패했습니다.');
    }
  };

  const handleSmsVerify = async code => {
    const ret = await fetchData({
      url: `${Config.API_SERVER}mail/verifySms`,
      method: 'post',
      data: { adminId: form.adminId.trim(), mobileAuthNo: Number(code) },
    });
    // ret이 없으면(네트워크 오류·세션 만료) "인증번호 불일치"가 아니라 별도 오류로 취급한다.
    if (!ret || ret.status >= 400) {
      throw new Error(ret?.data?.message || 'SMS 인증 확인에 실패했습니다.');
    }
    const ok = Boolean(ret?.data?.data?.verifyResult ?? ret?.data?.verifyResult);
    if (ok) setVerifiedMobile(form.mobile.trim());
    return ok;
  };

  const onCheckDuplicateId = async () => {
    if (editingId) return;
    const adminId = form.adminId.trim();
    if (!adminId) {
      window.alert('아이디를 입력해 주세요.');
      return;
    }
    const ret = await fetchData({
      url: `${Config.API_SERVER}idCheck`,
      method: 'post',
      data: { adminId },
    });
    const isDuplicated = Boolean(
      ret?.data?.data?.dupResult ?? ret?.data?.dupResult
    );
    if (isDuplicated) {
      setIdCheckPassed(false);
      setIdCheckedValue(adminId);
      window.alert('이미 사용 중인 아이디입니다.');
      return;
    }
    setIdCheckPassed(true);
    setIdCheckedValue(adminId);
    window.alert('사용 가능한 아이디입니다.');
  };

  const onEdit = row => {
    setEditingId(row.adminId);
    setForm({
      adminId: row.adminId || '',
      password: '',
      passwordConfirm: '',
      adminName: row.adminName || '',
      mobile: row.mobile || '',
    });
    setIdCheckedValue(row.adminId || '');
    setIdCheckPassed(true);
    // 수정 화면은 SMS 인증 대상이 아니므로(위 주석 참고) verifiedMobile을 그냥
    //  현재 값으로 맞춰 mobileVerified 체크에 걸리지 않게 한다.
    setVerifiedMobile(row.mobile || '');
  };

  const onDelete = async adminId => {
    if (!window.confirm('직원 계정을 삭제하시겠습니까?')) return;
    await fetchData({
      url: `${Config.API_SERVER}staff/${adminId}`,
      method: 'delete',
    });
    await loadList(currentPage);
  };

  if (!isOwner) {
    return (
      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="staff" />
          <section className="rightcontant_box">
            <h2 className="tit_01">{t.staffManagement || '직원 관리'}</h2>
            <div style={{ padding: '24px 0' }}>
              접근 권한이 없습니다. 사장님(admin) 계정으로 로그인해 주세요.
            </div>
          </section>
        </section>
      </main>
    );
  }

  return (
    <>
      <SmsVerifyModal
        open={smsModalOpen}
        onClose={() => setSmsModalOpen(false)}
        maskedPhone={form.mobile}
        onSend={handleSmsSend}
        onVerify={handleSmsVerify}
        t={t}
      />
      <section className="location_box location_box_mypage">
        <ul className="location_001">
          <li>
            <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>
              {t.home}
            </NavLink>
          </li>
          <li>{t.staffManagement || '직원 관리'}</li>
        </ul>
      </section>
      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="staff" />
          <section className="rightcontant_box">
            <h2 className="tit_01">{t.staffManagement || '직원 관리'}</h2>
            <article className="search_card">
              <div className="search_row">
                <div className="search_field">
                  <span className="field_label">검색어</span>
                  <input
                    value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                    placeholder="아이디/이름"
                  />
                </div>
              </div>
            </article>
            <article className="form_card contants_box_03">
              <table className="member_box">
                <tbody>
                  <tr>
                    <th>
                      직원 아이디 <span style={{ color: '#cc0000' }}>*</span>
                    </th>
                    <td>
                      <div
                        style={{
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                        }}
                      >
                        <input
                          className="input_01"
                          value={form.adminId}
                          disabled={!!editingId}
                          onChange={e => {
                            const value = e.target.value;
                            setForm({ ...form, adminId: value });
                            setIdCheckPassed(false);
                            // SMS 인증은 (adminId, mobile) 조합으로 서버에 저장되므로
                            //  아이디가 바뀌면 이전 인증은 이 새 아이디에 대해선 무효다.
                            setVerifiedMobile('');
                          }}
                        />
                        {!editingId ? (
                          <button
                            type="button"
                            className="button_05 button_gray_line"
                            onClick={onCheckDuplicateId}
                          >
                            중복확인
                          </button>
                        ) : null}
                      </div>
                      {!editingId && form.adminId.trim() ? (
                        <div
                          style={{
                            marginTop: '6px',
                            fontSize: '12px',
                            color:
                              idCheckPassed &&
                              idCheckedValue === form.adminId.trim()
                                ? '#2e7d32'
                                : '#cc0000',
                          }}
                        >
                          {idCheckPassed &&
                          idCheckedValue === form.adminId.trim()
                            ? '중복확인 완료'
                            : '중복확인이 필요합니다'}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                  <tr>
                    <th>
                      비밀번호{' '}
                      {!editingId ? (
                        <span style={{ color: '#cc0000' }}>*</span>
                      ) : null}
                    </th>
                    <td>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <input
                          className="input_01"
                          type={showPassword ? 'text' : 'password'}
                          placeholder={editingId ? '변경 시에만 입력' : ''}
                          value={form.password}
                          onChange={e =>
                            setForm({ ...form, password: e.target.value })
                          }
                          style={{ paddingRight: 34, boxSizing: 'border-box' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(v => !v)}
                          aria-label={
                            showPassword ? '비밀번호 숨기기' : '비밀번호 보기'
                          }
                          style={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: 16,
                            lineHeight: 1,
                            padding: 0,
                          }}
                        >
                          {showPassword ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" /><circle cx="12" cy="12" r="3" /></svg>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>
                      비밀번호 확인{' '}
                      {!editingId ? (
                        <span style={{ color: '#cc0000' }}>*</span>
                      ) : null}
                    </th>
                    <td>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <input
                          className="input_01"
                          type={showPasswordConfirm ? 'text' : 'password'}
                          placeholder={editingId ? '변경 시에만 입력' : ''}
                          value={form.passwordConfirm}
                          onChange={e =>
                            setForm({ ...form, passwordConfirm: e.target.value })
                          }
                          style={{ paddingRight: 34, boxSizing: 'border-box' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswordConfirm(v => !v)}
                          aria-label={
                            showPasswordConfirm
                              ? '비밀번호 숨기기'
                              : '비밀번호 보기'
                          }
                          style={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: 16,
                            lineHeight: 1,
                            padding: 0,
                          }}
                        >
                          {showPasswordConfirm ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" /><circle cx="12" cy="12" r="3" /></svg>
                          )}
                        </button>
                      </div>
                      {passwordTouched ? (
                        <div
                          style={{
                            marginTop: '6px',
                            fontSize: '12px',
                            color: passwordMatched ? '#2e7d32' : '#cc0000',
                          }}
                        >
                          {passwordMatched
                            ? '비밀번호가 일치합니다'
                            : '비밀번호가 일치하지 않습니다'}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                  <tr>
                    <th>
                      이름 <span style={{ color: '#cc0000' }}>*</span>
                    </th>
                    <td>
                      <input
                        className="input_01"
                        value={form.adminName}
                        onChange={e =>
                          setForm({ ...form, adminName: e.target.value })
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>
                      휴대폰 전화번호 <span style={{ color: '#cc0000' }}>*</span>
                    </th>
                    <td>
                      <div
                        style={{
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                        }}
                      >
                        <input
                          className="input_01"
                          value={form.mobile}
                          placeholder="010-1234-5678"
                          onChange={e =>
                            setForm({ ...form, mobile: e.target.value })
                          }
                        />
                        {!editingId ? (
                          <button
                            type="button"
                            className="button_05 button_gray_line"
                            onClick={() => setSmsModalOpen(true)}
                            disabled={!form.mobile.trim()}
                          >
                            {mobileVerified ? '재인증' : '인증하기'}
                          </button>
                        ) : null}
                      </div>
                      {!editingId ? (
                        <div
                          style={{
                            marginTop: '6px',
                            fontSize: '12px',
                            color: mobileVerified ? '#2e7d32' : '#cc0000',
                          }}
                        >
                          {mobileVerified ? '인증 완료' : '휴대폰 번호 인증이 필요합니다'}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                </tbody>
              </table>
            </article>
            <p className="font_gray">
              SMS 비용 부담으로 직원등록은 1명만 가능합니다.
            </p>
            <div className="button_box_01">
              <button
                className="button_01 button_red"
                onClick={onSubmit}
                disabled={!editingId && totalCount >= 1}
              >
                {editingId ? '직원 수정' : '직원 추가'}
              </button>
              {editingId ? (
                <button
                  className="button_01 button_gray_line"
                  onClick={() => {
                    setEditingId('');
                    setForm(EMPTY_FORM);
                    setVerifiedMobile('');
                  }}
                >
                  취소
                </button>
              ) : null}
            </div>
            <p className="table_01" style={{ marginBottom: '16px' }}>
              전체 직원 목록 (총 {totalCount}명)
            </p>
            <div className="table_wrapper">
              <table className="table_box_01">
                <thead>
                  <tr>
                    <th>아이디</th>
                    <th>이름</th>
                    <th>휴대폰</th>
                    <th>상태</th>
                    <th>등록일</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        style={{ textAlign: 'center', padding: '32px' }}
                      >
                        데이터가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    list.map(row => (
                      <tr key={row.adminNo}>
                        <td>{row.adminId}</td>
                        <td>{row.adminName || '-'}</td>
                        <td>{row.mobile || '-'}</td>
                        <td>{row.state === 'U' ? '사용중' : row.state}</td>
                        <td>
                          {row.regDttm ? String(row.regDttm).slice(0, 10) : '-'}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="button_05 button_gray_line"
                            onClick={() => onEdit(row)}
                            style={{ marginRight: '8px' }}
                          >
                            수정
                          </button>
                          <button
                            type="button"
                            className="button_05 button_black"
                            onClick={() => onDelete(row.adminId)}
                          >
                            삭제
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <ul className="page_box">
              <Pagination totalPage={totalPage} onPageChange={setCurrentPage} />
            </ul>
          </section>
        </section>
      </main>
    </>
  );
}
